//React
import React, { useEffect, useRef, useState } from "react";
//Components
import {
  Button,
  Dropdown,
  FlexBox,
  Image,
  Input,
  Modal,
  Screen,
  TranslatedString,
  Code,
  Header,
} from "components";
//Paths
import ROUTE_PATH from "Routes/paths";
//Styles
import "./styles.scss";
//Libraries
import cx from "classnames";
import ResizePanel from "react-resize-panel-ts";
//Assets
import Logo from "assets/logo.svg";
//Utils
import { ChangeHistory, copyText, useIsInViewport } from "utils";

let scriptCode = `<!-- OTPless SDK -->
<script type="text/javascript" src="https://otpless.com/auth.js"></script>`;
let divCode = `<!-- WhatsApp Login Button -->
<div id="otpless"></div>`;
let callbackCode = `useEffect(() => {
  // Define the 'otpless' function
  window.otpless = () => {
    // Retrieve the user's details after successful login
    const userDetails = window.otplessUser;
    const waName = userDetails.waName;
    const waNumber = userDetails.waNumber;

    // Handle the signup/signin process
    // ...
  };
}, []);`;
const Landing = () => {
  const [copyButtonText, setCopyButtonText] = useState("Copy Code");
  const [activeCodeTab, setActiveCodeTab] = useState("");
  const [isScrollRouteAllowed, setIsScrollRouteAllowed] = useState(true);
  const ScriptSectionRef: any = useRef();
  const isScriptSectionInViewport = useIsInViewport(ScriptSectionRef);
  const DivSectionRef: any = useRef();
  const isDicSectionInViewport = useIsInViewport(DivSectionRef);
  const CallbackSectionRef: any = useRef();
  const isCallbackSectionInViewport = useIsInViewport(CallbackSectionRef);

  const scrollAccToSpecificElementMap = {
    "Step 1": ScriptSectionRef,
    "Step 2": DivSectionRef,
    "Step 3": CallbackSectionRef,
  };

  useEffect(() => {
    setActiveCodeTab("Step 1");
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setIsScrollRouteAllowed(true);
    }, 500);
  }, [activeCodeTab]);

  useEffect(() => {
    if (isScrollRouteAllowed) {
      if (isScriptSectionInViewport) {
        setActiveCodeTab("Step 1");
      } else if (isDicSectionInViewport) {
        setActiveCodeTab("Step 2");
      } else if (isCallbackSectionInViewport) {
        setActiveCodeTab("Step 3");
      }
    }
  }, [
    isScrollRouteAllowed,
    isScriptSectionInViewport,
    isDicSectionInViewport,
    isCallbackSectionInViewport,
  ]);

  //Scroll To Specific element
  const scrollToSpecificElement = (selectedTab) => {
    scrollAccToSpecificElementMap[selectedTab]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  };

  const MiddleSection = () => {
    return (
      <div className="middle-section text-center">
        <FlexBox
          justifyCenter
          flexColumn
          className="h-100 pd-20 child-section script-section"
          flexBoxRef={ScriptSectionRef}
        >
          <p className="fs-22 font-medium text-left">Install the OTPless SDK</p>
          <p className="clr-gray fs-16 text-left">
            Copy-paste the code in your app's{" "}
            <span className="clr-theme"> public/index.html </span> file to
            install the SDK.
          </p>
        </FlexBox>
        <FlexBox
          justifyCenter
          flexColumn
          className="h-100 pd-20 child-section div-section"
          flexBoxRef={DivSectionRef}
        >
          <p className="fs-22 font-medium text-left">Create Button</p>
          <p className="clr-gray fs-16 text-left">
            Copy-paste the code to create a WhatsApp Login button in your{" "}
            <span className="clr-theme">app's login page</span>
          </p>
        </FlexBox>
        <FlexBox
          justifyCenter
          flexColumn
          className="h-100 pd-20 child-section callback-section"
          flexBoxRef={CallbackSectionRef}
        >
          <p className="fs-22 font-medium text-left">Do Signup/Signin</p>
          <p className="clr-gray fs-16 text-left">
            Copy-paste the code in your login page to retrieve the user's
            WhatsApp name and number.
          </p>
        </FlexBox>
        {/* <FlexBox alignCenter className="h-5p disabled o-0 pd-20"></FlexBox> */}
      </div>
    );
  };

  const BottomSection = () => {
    return (
      <FlexBox
        justifyBetween
        flexColumn
        className="bottom-section clr-white pd-20"
      >
        <div className="h-70p w-100">
          <FlexBox alignCenter className="clr-white code-tabs mr-b-20">
            <p
              className={cx(
                activeCodeTab === "Step 1" && "active",
                "fs-14 full-border pd-v-5 pd-h-10 br-9 clicking-animation"
              )}
              onClick={() => {
                setActiveCodeTab("Step 1");
                scrollToSpecificElement("Step 1");
                setIsScrollRouteAllowed(false);
              }}
            >
              Step 1
            </p>
            <p
              className={cx(
                activeCodeTab === "Step 2" && "active",
                "fs-14 full-border pd-v-5 pd-h-10 br-9 clicking-animation"
              )}
              onClick={() => {
                setActiveCodeTab("Step 2");
                scrollToSpecificElement("Step 2");
                setIsScrollRouteAllowed(false);
              }}
            >
              Step 2
            </p>
            <p
              className={cx(
                activeCodeTab === "Step 3" && "active",
                "fs-14 full-border pd-v-5 pd-h-10 br-9 clicking-animation"
              )}
              onClick={() => {
                setActiveCodeTab("Step 3");
                scrollToSpecificElement("Step 3");
                setIsScrollRouteAllowed(false);
              }}
            >
              Step 3
            </p>
          </FlexBox>
          <Code
            code={
              activeCodeTab === "Step 1"
                ? scriptCode
                : activeCodeTab === "Step 2"
                ? divCode
                : callbackCode
            }
            language={activeCodeTab === "Step 3" ? "java" : "html"}
          />
        </div>
        <FlexBox alignEnd className="h-15p w-100">
          <Button
            text={copyButtonText}
            className="w-100 fs-14"
            onClick={() => {
              copyText(
                activeCodeTab === "Step 1"
                  ? scriptCode
                  : activeCodeTab === "Step 2"
                  ? divCode
                  : callbackCode
              );
              setCopyButtonText("Copied");
              setTimeout(() => {
                setCopyButtonText("Copy Code");
              }, 2000);
            }}
          />
        </FlexBox>
      </FlexBox>
    );
  };

  //Return UI
  return (
    <Screen
      className="react-js-screen"
      screenAnimationClass="no-animation"
      hasHeader
      customHeaderDetails={
        <Button
          variant="outline"
          className="fs-10 pd-5 w-30"
          text="Platforms"
          onClick={() => {
            ChangeHistory("back", ROUTE_PATH.PLATFORM.HOME);
          }}
        />
      }
    >
      <div className="h-100 w-100 overflow-hdn">
        {MiddleSection()}
        {BottomSection()}
      </div>
    </Screen>
  );
};

export default Landing;
